import {
  AddFamilyMemberPayload,
  AddFamilyMemberResponse,
  FamilyData,
  FamilyInfo,
  FamilyInvite,
  FamilyManageLicenseData,
  InviteCreateResponse,
  RepurchasePreviewData,
  SwitchProfilePayload,
} from '~/familyAccount/types/familyAccounts.types'
import { FAMILY_API_TAGS, USER_API_TAGS } from '~/redux/constants/tags.const'
import { IUser } from '~/types/user-info-types'

import { invalidateOnSuccess } from '../utils/util'

import { homApi } from './hom'
import { AuthenticationResponse, Result } from './types'

const familyApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    getFamily: builder.query<
      { familyProfiles: FamilyData[]; hasValidSubscription: boolean; isAdminPopupDismissed: boolean },
      undefined
    >({
      query: () => ({
        url: '/v1/family/get-family',
        method: 'GET',
      }),
      providesTags: ['family'],
    }),
    getFamilyInfo: builder.query<FamilyInfo, undefined>({
      query: () => ({
        url: '/v1/family/get-family-info',
        method: 'GET',
      }),
      providesTags: ['family'],
    }),
    getFamilyMember: builder.query<{ user: IUser; hasPin: boolean }, { userId: number }>({
      query: (params) => ({
        url: '/v1/family/get-family-member',
        method: 'GET',
        params,
      }),
    }),

    updateFamilyMember: builder.mutation<Result<unknown>, { userId: number; nickname?: string; profileColor?: string }>(
      {
        query: (body) => ({
          url: '/v1/family/update-family-member',
          method: 'POST',
          body,
        }),
        invalidatesTags: invalidateOnSuccess(['family']),
      },
    ),
    addFamilyMember: builder.mutation<AddFamilyMemberResponse, AddFamilyMemberPayload>({
      query: (body) => ({
        url: '/v1/family/add-email-profile',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),

    switchProfile: builder.mutation<Result<AuthenticationResponse>, SwitchProfilePayload>({
      query: (body) => ({
        url: '/v1/family/switch-profile',
        method: 'POST',
        body,
      }),
    }),
    setAdminPopupDismissed: builder.mutation<Result<unknown>, void>({
      query: (body) => ({
        url: '/v1/family/popup-dismissed',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),
    setPin: builder.mutation<Result<unknown>, { targetUserId?: number; pin: string; token?: string }>({
      query: (body) => ({
        url: '/v1/family/set-pin',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),
    setFamilyLogin: builder.mutation<
      { type: string; email: string; hasPassword: boolean },
      { email?: string; password?: string; currentPassword?: string }
    >({
      query: (body) => ({
        url: '/v1/family/set-family-login',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([USER_API_TAGS.UserMe, FAMILY_API_TAGS.Family]),
    }),
    validatePin: builder.mutation<
      Result<{ data: { type: string } } | { error: { data: { message: string } } }>,
      { pin: string; userId?: number }
    >({
      query: (body) => ({
        url: '/v1/family/validate-pin',
        method: 'POST',
        body,
      }),
    }),
    deletePin: builder.mutation<Result<unknown>, { userId?: number }>({
      query: (body) => ({
        url: '/v1/family/delete-pin',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),
    createFamilyInvite: builder.mutation<InviteCreateResponse, undefined>({
      query: () => ({
        url: '/v1/family/invite',
        method: 'POST',
      }),
    }),
    getFamilyInvites: builder.query<FamilyInvite[], { familyId: number }>({
      query: (params) => ({
        url: '/v1/family/get-invites',
        method: 'GET',
        params,
      }),
      providesTags: ['family'],
    }),
    getFamilyInviterData: builder.query<
      {
        adminId: number
        adminAvatarUrl: string | null
        adminEmail: string
        adminNickname: string
        adminColor: string | null
        error?: { data: { message: string } }
      },
      { inviteCode: string }
    >({
      query: (params) => ({
        url: '/v1/family/invite/get-admin',
        method: 'GET',
        params,
      }),
    }),
    sendFamilyInvite: builder.mutation<{ message: string }, { inviteCode: string; email: string }>({
      query: (body) => ({
        url: '/v1/family/invite/send',
        method: 'POST',
        body,
      }),
    }),
    acceptFamilyInvite: builder.mutation<
      { success?: boolean; error?: { data: { message: string } } },
      { inviteCode: string }
    >({
      query: (body) => ({
        url: '/v1/family/invite/accept',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),
    cancelFamilyInvite: builder.mutation<Result<unknown>, { inviteCode: string }>({
      query: (body) => ({
        url: '/v1/family/invite/cancel',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),
    manageLicense: builder.mutation<
      Result<{ success: boolean; message: string }> | Result<{ requiresPayment: boolean; paymentUrl: string }>,
      { action: 'add' | 'remove'; licenseCount: number; prorationDate: number; userId?: number }
    >({
      query: (body) => ({
        url: '/v1/family/manage-license',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),
    manageLicensePreview: builder.mutation<
      FamilyManageLicenseData,
      {
        action: 'add' | 'remove' | 'upgrade'
        licenseCount: number
        priceId?: string
        // Preview against the discount the change would apply, so the amount shown is what is charged
        couponCode?: string
        promotionId?: string
      }
    >({
      query: (body) => ({
        url: '/v1/family/manage-license/preview',
        method: 'POST',
        body,
      }),
    }),
    getRepurchaseCostPreview: builder.query<
      RepurchasePreviewData,
      {
        licenseCount: number
        partner?: 'dnb' | 'knowit'
        couponCode?: string
        promotionId?: string
        /** They arrived from a price or campaign page, so that offer wins over their old plan's */
        fromPricePage?: boolean
      }
    >({
      query: (body) => ({
        url: '/v1/family/repurchase-license/preview',
        method: 'POST',
        body,
      }),
    }),
    cancelLicenseRemoval: builder.mutation<Result<unknown>, { userId?: number }>({
      query: (body) => ({
        url: '/v1/family/cancel-license-removal',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess(['family']),
    }),
    forgotPin: builder.mutation<Result<unknown>, void>({
      query: () => ({
        url: '/v1/family/forgot-pin',
        method: 'POST',
      }),
    }),
    verifyForgotPinToken: builder.mutation<{ valid: boolean; userId?: number }, { token: string }>({
      query: (body) => ({
        url: '/v1/family/forgot-pin/verify-token',
        method: 'POST',
        body,
      }),
    }),
    assignMathAccess: builder.mutation<Result<{ success: boolean }>, { userId: number }>({
      query: (body) => ({
        url: '/v1/family/assign-math-access',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['family'],
    }),
  }),
})

export const {
  useGetFamilyQuery,
  useGetFamilyInfoQuery,
  useAddFamilyMemberMutation,
  useSwitchProfileMutation,
  useSetAdminPopupDismissedMutation,
  useSetPinMutation,
  useSetFamilyLoginMutation,
  useValidatePinMutation,
  useDeletePinMutation,
  useCreateFamilyInviteMutation,
  useGetFamilyInvitesQuery,
  useGetFamilyInviterDataQuery,
  useSendFamilyInviteMutation,
  useAcceptFamilyInviteMutation,
  useGetFamilyMemberQuery,
  useUpdateFamilyMemberMutation,
  useCancelFamilyInviteMutation,
  useManageLicenseMutation,
  useManageLicensePreviewMutation,
  useGetRepurchaseCostPreviewQuery,
  useCancelLicenseRemovalMutation,
  useForgotPinMutation,
  useVerifyForgotPinTokenMutation,
  useAssignMathAccessMutation,
  useLazyGetFamilyQuery,
  useLazyGetFamilyInfoQuery,
} = familyApi
export const { switchProfile } = familyApi.endpoints
